import { useToolOwnership } from "./call/shared/toolOwnership"
import { useCallback, useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { CheckCircle2, EyeOff, Lock, PencilLine, RefreshCw, Send, Share2, Sparkles, X } from "lucide-react"

import { Button } from "components/ui/button"
import { Textarea } from "components/ui/textarea"
import { meetings } from "services/api/meetingsClient"
import type {
  MeetingInspireActivity,
  MeetingInspirePanelRole,
  MeetingInspireResponse,
} from "services/api/meetingsClient"
import {
  INSPIRE_CONTROL_MESSAGE_TYPE,
  isInspireControlAckMessage,
  type InspireControlAction,
} from "./inspireControlMessages"
import { useMeetingPanelEvents } from "./useMeetingPanelEvents"
import type { InspireEmbedContext } from "./call/types"

const PANEL_POLL_INTERVAL_MS = 30000
const CONTROL_ACK_TIMEOUT_MS = 3000
/** The riffle (820 ms) plus the card flying in after it (starts at 900 ms, lasts 360 ms). */
const INSPIRE_DEAL_ANIMATION_MS = 1300
/** Cards this page has already dealt, so remounting the panel (stage and tab) does not deal again. */
const dealtActivityIds = new Set<string>()

const formatResponseTime = (iso: string) => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

const cardInk = (hex: string) => {
  const normalized = hex.replace("#", "")
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return "#172033"
  const [red, green, blue] = [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16))
  return (red * 299 + green * 587 + blue * 114) / 1000 > 145 ? "#172033" : "#fffdf8"
}

export type MeetingInspirePanelPreview = {
  role: MeetingInspirePanelRole
  branding?: { name: string; logo_url?: string }
  activity: MeetingInspireActivity | null
  response?: MeetingInspireResponse | null
  responses?: MeetingInspireResponse[]
  sharedResponse?: MeetingInspireResponse | null
  onSaveResponse?: (body: string) => MeetingInspireResponse
  onShareResponse?: (response: MeetingInspireResponse) => void
  onStopSharing?: () => void
  onClose?: () => void
}

function InspireSession({ preview, embed }: { preview?: MeetingInspirePanelPreview; embed?: InspireEmbedContext }) {
  const { meetingId: routeMeetingId } = useParams<{ meetingId: string }>()
  const [searchParams] = useSearchParams()
  const meetingId = embed?.meetingId || routeMeetingId
  const panelToken = embed?.panelToken || searchParams.get("panel_token") || ""
  const ownership = useToolOwnership(`${meetingId}:${panelToken}`)
  const captureTool = ownership.capture
  const [role, setRole] = useState<MeetingInspirePanelRole>(preview?.role || "listener")
  const [participantView, setParticipantView] = useState(false)
  const [branding, setBranding] = useState({ name: preview?.branding?.name || "WiseFriend", logo_url: preview?.branding?.logo_url || "" })
  const [activity, setActivity] = useState<MeetingInspireActivity | null>(preview?.activity || null)
  const [response, setResponse] = useState<MeetingInspireResponse | null>(preview?.response || null)
  const [responses, setResponses] = useState<MeetingInspireResponse[]>(preview?.responses || [])
  const [sharedResponse, setSharedResponse] = useState<MeetingInspireResponse | null>(preview?.sharedResponse || null)
  const [responseCount, setResponseCount] = useState(preview?.responses?.length || 0)
  const [nextPage, setNextPage] = useState<number | null>(null)
  const [draft, setDraft] = useState(preview?.response?.body || "")
  const [editing, setEditing] = useState(!preview?.response)
  const editingRef = useRef(editing)
  editingRef.current = editing
  const [loading, setLoading] = useState(!preview && Boolean(meetingId && panelToken))
  const [refreshing, setRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pendingControl, setPendingControl] = useState<InspireControlAction | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(preview || (meetingId && panelToken) ? "" : "This Inspire Card link is unavailable.")
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const loadInFlightRef = useRef(false)
  const pendingLoadRef = useRef(false)
  const ackTimerRef = useRef<number | null>(null)
  const activityIdRef = useRef<string | null>(preview?.activity?.id || null)
  const saveGenerationRef = useRef(0)
  const responsesGenerationRef = useRef(0)

  useEffect(() => () => {
    saveGenerationRef.current += 1
  }, [ownership.key])

  useEffect(() => {
    setLoadingMore(false)
    setNextPage(null)
    return () => {
      responsesGenerationRef.current += 1
    }
  }, [meetingId, panelToken, preview])

  const loadPanel = useCallback(async (silent = false) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (preview) return
    if (loadInFlightRef.current) {
      pendingLoadRef.current = true
      return
    }
    if (!meetingId || !panelToken) {
      if (!meetingId || !panelToken) {
        setError("This Inspire Card link is unavailable.")
        setLoading(false)
      }
      return
    }
    do {
      pendingLoadRef.current = false
      loadInFlightRef.current = true
      try {
        const payload = await meetings.getMeetingInspirePanel(meetingId, panelToken)
      if (!ownsTool()) return;
        setRole(payload.role)
        setBranding({ name: payload.branding?.name || "WiseFriend", logo_url: payload.branding?.logo_url || "" })
        setRealtimeUrl(payload.realtime_url || null)
        const nextSharedResponse = payload.shared_response || null
        const nextActivity = payload.active_activity || payload.shared_activity || (
          payload.role === "presenter" ? payload.latest_activity || null : null
        )
        const activityChanged = activityIdRef.current !== (nextActivity?.id || null)
        activityIdRef.current = nextActivity?.id || null
        if (activityChanged) {
          saveGenerationRef.current += 1
          setSaving(false)
        }
        setActivity(nextActivity)
        setSharedResponse(nextSharedResponse)
        // A panel refresh replaces the response list, so pages from its previous snapshot are stale.
        responsesGenerationRef.current += 1
        setLoadingMore(false)
        if (payload.role === "presenter") {
          setResponses(payload.responses || [])
          setResponseCount(payload.responses_count || 0)
          setNextPage((payload.responses_count || 0) > (payload.responses || []).length ? 2 : null)
        }
        const saved = payload.response || null
        setResponse(saved)
        // A refresh may have started before the attendee began revising their saved answer.
        if (activityChanged || (saved && !editingRef.current)) {
          setDraft(saved?.body || "")
          setEditing(!saved)
        }
        setError("")
      } catch (err: any) {
      if (!ownsTool()) return;
        if (!silent) setError(err?.response?.data?.detail || err?.message || "Unable to load the Inspire Card.")
      } finally {
      if (!ownsTool()) return;
        loadInFlightRef.current = false
        setLoading(false)
      }
    } while (pendingLoadRef.current)
  }, [meetingId, panelToken, preview, captureTool])

  useEffect(() => {
    if (!preview) return
    const nextActivity = preview.activity || null
    const activityChanged = activityIdRef.current !== (nextActivity?.id || null)
    activityIdRef.current = nextActivity?.id || null
    if (activityChanged) {
      saveGenerationRef.current += 1
      setSaving(false)
    }
    setRole(preview.role)
    setBranding({ name: preview.branding?.name || "WiseFriend", logo_url: preview.branding?.logo_url || "" })
    setActivity(nextActivity)
    setSharedResponse(preview.sharedResponse || null)
    setResponses(preview.responses || [])
    setResponseCount(preview.responses?.length || 0)
    const saved = preview.response || null
    setResponse(saved)
    if (activityChanged) {
      setDraft(saved?.body || "")
      setEditing(!saved)
    }
    setLoading(false)
    setError("")
  }, [preview])

  useEffect(() => {
    if (preview) return
    if (!meetingId || !panelToken) return
    // Refresh when the editor opens or closes, including after a successful response save.
    void loadPanel()
    const intervalId = window.setInterval(() => void loadPanel(true), PANEL_POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [editing, loadPanel, meetingId, panelToken, preview])

  useMeetingPanelEvents({ onReconnect: () => void loadPanel(true), panelToken,
    realtimeUrl,
    enabled: !preview && Boolean(meetingId && panelToken),
    onEvent: (event) => {
      if (event.type.startsWith("inspire.")) void loadPanel(true)
    },
  })

  // A newly dealt card arrives with the same shuffle the presenter sees when drawing one.
  const [dealing, setDealing] = useState(false)
  const dealActivityId = !preview && activity?.status === "active" ? activity.id : null
  useEffect(() => {
    if (!dealActivityId || dealtActivityIds.has(dealActivityId)) return
    dealtActivityIds.add(dealActivityId)
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    setDealing(true)
    let finished = false
    const timerId = window.setTimeout(() => {
      finished = true
      setDealing(false)
    }, INSPIRE_DEAL_ANIMATION_MS)
    return () => {
      window.clearTimeout(timerId)
      setDealing(false)
      // Interrupted (or a development double mount): let the next mount deal it.
      if (!finished) dealtActivityIds.delete(dealActivityId)
    }
  }, [dealActivityId])

  const embedRefreshKey = embed?.refreshKey
  useEffect(() => {
    if (!embedRefreshKey) return
    void loadPanel(true)
  }, [embedRefreshKey, loadPanel])

  useEffect(() => {
    if (embed) return
    const handleAck = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isInspireControlAckMessage(event.data)) return
      if (event.data.meetingId !== meetingId) return
      if (ackTimerRef.current) window.clearTimeout(ackTimerRef.current)
      ackTimerRef.current = null
      setPendingControl(null)
      if (!event.data.accepted) setError("The Daily call could not apply that action.")
    }
    window.addEventListener("message", handleAck)
    return () => {
      window.removeEventListener("message", handleAck)
      if (ackTimerRef.current) window.clearTimeout(ackTimerRef.current)
    }
  }, [embed, meetingId])

  const postControl = (action: InspireControlAction, activityId: string) => {
    if (!meetingId) return
    if (embed) {
      embed.onControlAction(action, activityId)
        .then((accepted) => {
          setPendingControl(null)
          if (!accepted) setError("The call could not apply that action.")
        })
        .catch(() => {
          setPendingControl(null)
          setError("The call could not apply that action.")
        })
      return
    }
    window.top?.postMessage({ type: INSPIRE_CONTROL_MESSAGE_TYPE, meetingId, activityId, action }, window.location.origin)
    ackTimerRef.current = window.setTimeout(() => {
      setPendingControl(null)
      setError("The meeting did not respond. Open this panel inside the Daily call and try again.")
    }, CONTROL_ACK_TIMEOUT_MS)
  }

  const shareResponse = async (item: MeetingInspireResponse) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!activity || activity.status !== "ended" || pendingControl) return
    if (preview) {
      setSharedResponse(item)
      preview.onShareResponse?.(item)
      return
    }
    if (!meetingId) return
    setPendingControl("share")
    setError("")
    try {
      const result = await meetings.shareMeetingInspireResponse(meetingId, activity.id, panelToken, item.id)
      if (!ownsTool()) return;
      setSharedResponse(result.shared_response)
      postControl("share", activity.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPendingControl(null)
      setError(err?.response?.data?.detail || err?.message || "Unable to share this response.")
    }
  }

  const stopSharing = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!activity || !sharedResponse || pendingControl) return
    if (preview) {
      setSharedResponse(null)
      preview.onStopSharing?.()
      return
    }
    if (!meetingId) return
    setPendingControl("unshare")
    setError("")
    try {
      await meetings.shareMeetingInspireResponse(meetingId, activity.id, panelToken, null)
      if (!ownsTool()) return;
      setSharedResponse(null)
      postControl("unshare", activity.id)
    } catch (err: any) {
      if (!ownsTool()) return;
      setPendingControl(null)
      setError(err?.response?.data?.detail || err?.message || "Unable to stop sharing this response.")
    }
  }

  const saveResponse = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const body = draft.trim()
    if (!activity || activity.status !== "active" || !body || saving) return
    if (preview) {
      const now = new Date().toISOString()
      const saved = preview.onSaveResponse?.(body) || {
        id: response?.id || "preview-response",
        display_name: "Preview learner",
        body,
        created_at: response?.created_at || now,
        updated_at: now,
      }
      setResponse(saved)
      setDraft(saved.body)
      setEditing(false)
      return
    }
    if (!meetingId) return
    const saveGeneration = ++saveGenerationRef.current
    setSaving(true)
    setError("")
    try {
      const saved = await meetings.saveMeetingInspireResponse(meetingId, activity.id, panelToken, { body })
      if (!ownsTool()) return;
      if (saveGeneration !== saveGenerationRef.current) return
      setResponse(saved)
      setDraft(saved.body)
      setEditing(false)
    } catch (err: any) {
      if (!ownsTool()) return;
      if (saveGeneration !== saveGenerationRef.current) return
      setError(err?.response?.data?.detail || err?.response?.data?.body?.[0] || "Unable to submit your response.")
    } finally {
      if (!ownsTool()) return;
      if (saveGeneration === saveGenerationRef.current) setSaving(false)
    }
  }

  const loadMore = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !activity || !nextPage || loadingMore) return
    const responsesGeneration = responsesGenerationRef.current
    setLoadingMore(true)
    try {
      const page = await meetings.getMeetingInspireResponses(meetingId, activity.id, panelToken, nextPage)
      if (!ownsTool()) return;
      if (responsesGeneration !== responsesGenerationRef.current) return
      setResponses((current) => [...current, ...page.results])
      setNextPage(page.next_page || null)
      setResponseCount(page.count)
    } catch (err: any) {
      if (!ownsTool()) return;
      if (responsesGeneration !== responsesGenerationRef.current) return
      setError(err?.response?.data?.detail || "Unable to load more responses.")
    } finally {
      if (!ownsTool()) return;
      if (responsesGeneration === responsesGenerationRef.current) setLoadingMore(false)
    }
  }

  const isPresenter = role === "presenter"
  const anonymous = activity?.visibility_mode === "anonymous"
  const submissionsClosed = activity?.status === "ended"
  const deckAccent = activity?.deck_accent_color_snapshot || "#1F3A5F"
  const deckInk = cardInk(deckAccent)
  const showPanelHeader = isPresenter || !activity
  // Inside the call the stage or tools panel already frames the card, so it sits straight on the
  // paper background instead of inside a second white sheet.
  const framed = !embed

  return (
    <div data-testid="meeting-inspire-panel" data-embedded={embed ? "true" : undefined} className={`${preview || embed ? "absolute" : "fixed z-50"} inset-0 min-h-0 overflow-hidden bg-[#111827] text-slate-950`}>
      <div className={`h-full overflow-y-auto bg-[radial-gradient(circle_at_top_left,_#fff8e9,_#f2e6ce_52%,_#ded0b6)] ${framed ? "px-2 py-2 sm:px-4 sm:py-3" : "px-1 py-1 sm:px-3 sm:py-2"}`}>
        <main className="mx-auto flex min-h-full w-full max-w-5xl items-center justify-center">
          <section data-testid="inspire-panel-sheet" className={framed ? "w-full overflow-hidden rounded-2xl border border-[#d9c9aa] bg-[#fffdf8] shadow-[0_20px_60px_rgba(35,28,18,0.24)] sm:rounded-3xl" : "w-full"}>
            {framed && <div aria-hidden="true" className="h-1.5 sm:h-2" style={{ backgroundColor: deckAccent }} />}
            {showPanelHeader && <header data-testid="inspire-panel-header" className="flex items-center gap-2.5 border-b border-[#eadfc9] px-3 py-2 sm:gap-3 sm:px-7 sm:py-4">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="" className="h-8 w-8 rounded-full object-cover sm:h-10 sm:w-10" />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#263a5b] text-[#f7d58a] sm:h-10 sm:w-10">
                  <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-[#263a5b]">{branding.name}</p>
                <p className="truncate text-xs text-[#756951]">
                  {activity ? `Inspire Card · ${anonymous ? "Anonymous" : "Named"} responses` : "Inspire Cards"}
                </p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Refresh Inspire Card"
                onClick={async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return; if (preview) return; setRefreshing(true); await loadPanel();
      if (!ownsTool()) return; setRefreshing(false) }}
                className="h-9 w-9 min-h-0 p-0 text-[#263a5b]"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
            </header>}

            {isPresenter && activity && <div className="flex flex-wrap gap-2 border-b border-[#eadfc9] px-3 py-2 sm:px-7" role="group" aria-label="Inspire view">
              <Button type="button" size="auto" variant="outline" aria-pressed={!participantView} onClick={() => setParticipantView(false)} className="min-h-10 px-3 text-xs">Admin view</Button>
              <Button type="button" size="auto" variant="outline" aria-pressed={participantView} onClick={() => setParticipantView(true)} className="min-h-10 px-3 text-xs">Participant view</Button>
            </div>}

            {error && <div role="alert" className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:mx-7">{error}</div>}

            {loading ? (
              <div className="px-5 py-16 text-center text-sm text-[#756951]">Loading Inspire Card…</div>
            ) : !activity ? (
              <div className="px-6 py-16 text-center">
                <Sparkles className="mx-auto h-9 w-9 text-[#a06d32]" />
                <h1 className="mt-4 text-xl font-bold text-[#263a5b]">Waiting for the next Inspire Card</h1>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#756951]">The presenter will share a reflection prompt here when the group is ready.</p>
              </div>
            ) : (
              <div className={framed ? "px-2.5 py-2.5 sm:px-5 sm:py-4" : "px-1.5 py-1.5 sm:px-3 sm:py-2"}>
                <div className="relative mx-auto mb-2 max-w-3xl px-1 py-1 sm:mb-3 sm:px-4 sm:py-2">
                  <div data-testid="inspire-physical-card-backing" aria-hidden="true" className="absolute inset-x-3 inset-y-2 rotate-0 rounded-2xl opacity-30 sm:inset-x-8 sm:inset-y-3 sm:rounded-[1.75rem]" style={{ backgroundColor: deckAccent }} />
                  {dealing && (
                    <div data-testid="inspire-deal-animation" aria-label="Dealing the card" role="img" className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                      <div className="relative h-[150px] w-[104px] scale-75 [perspective:900px] motion-safe:animate-inspire-deck-square sm:scale-100" aria-hidden="true">
                        {Array.from({ length: 8 }, (_, index) => (
                          <span
                            key={index}
                            className={`absolute inset-0 block rounded-[0.8rem] border-2 border-white/70 p-1.5 shadow-[0_8px_20px_rgba(0,0,0,0.35)] [backface-visibility:hidden] [transform-style:preserve-3d] ${index % 2 === 0 ? "motion-safe:animate-inspire-riffle-left" : "motion-safe:animate-inspire-riffle-right"}`}
                            style={{ backgroundColor: deckAccent, zIndex: index + 1, animationDelay: `${Math.floor(index / 2) * 45}ms` }}
                          >
                            <span className="flex h-full w-full items-center justify-center rounded-[0.55rem] border border-white/45 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.18)_0,rgba(255,255,255,0.18)_3px,rgba(0,0,0,0.08)_3px,rgba(0,0,0,0.08)_6px)]">
                              <Sparkles className="h-7 w-7 rounded-full bg-black/15 p-1.5 text-white/90" />
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <article data-testid="inspire-physical-card" className={`relative flex min-h-[132px] rotate-0 flex-col rounded-2xl border border-black/10 p-3.5 shadow-[0_22px_55px_rgba(58,42,22,0.28)] sm:min-h-[200px] sm:rounded-[1.75rem] sm:p-6 ${dealing ? "motion-safe:animate-inspire-card-reveal" : ""}`} style={{ backgroundColor: deckAccent, color: deckInk, animationDelay: dealing ? "900ms" : undefined }}>
                    <div className="flex items-center justify-between gap-4 text-[10px] font-black uppercase tracking-[0.18em] opacity-75 sm:text-xs">
                      <span>{activity.deck_name_snapshot || "Inspire Deck"}</span>
                      {!isPresenter ? (
                        <span className="flex shrink-0 items-center gap-1.5">
                          {branding.logo_url ? (
                            <img data-testid="inspire-card-brand-mark" src={branding.logo_url} alt="" className="h-6 w-6 rounded-full border border-black/10 object-cover sm:h-8 sm:w-8" />
                          ) : (
                            <span data-testid="inspire-card-brand-mark" className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10 sm:h-8 sm:w-8">
                              <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                            </span>
                          )}
                          {!preview && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label="Refresh Inspire Card"
                              onClick={async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return; setRefreshing(true); await loadPanel();
      if (!ownsTool()) return; setRefreshing(false) }}
                              className="h-7 w-7 min-h-0 rounded-full bg-black/5 p-0 text-current hover:bg-black/10 sm:h-8 sm:w-8"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            </Button>
                          )}
                        </span>
                      ) : <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />}
                    </div>
                    <h1 className="mt-3 max-w-2xl text-lg font-black leading-5 sm:mt-5 sm:text-3xl sm:leading-tight">{activity.card_prompt_snapshot}</h1>
                    {activity.card_helper_text_snapshot && <p className="mt-2 max-w-2xl text-[11px] leading-4 opacity-80 sm:mt-3 sm:text-sm sm:leading-5">{activity.card_helper_text_snapshot}</p>}
                    <p className="mt-auto pt-3 text-[8px] font-bold uppercase tracking-[0.16em] opacity-55 sm:pt-4 sm:text-[10px]">Pause. Breathe. No speed round.</p>
                  </article>
                </div>

                {sharedResponse ? (
                  <div data-testid="inspire-shared-response" className="mx-auto max-w-3xl rounded-2xl border border-[#d8c8a9] bg-white px-5 py-6 shadow-sm sm:px-8 sm:py-8" aria-live="polite">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-[#8b622e]"><Share2 className="h-4 w-4" /> Shared with the group</p>
                      {isPresenter && (
                        <Button type="button" variant="outline" size="auto" onClick={stopSharing} disabled={Boolean(pendingControl)} className="min-h-9 px-3 text-xs">
                          <X className="h-4 w-4" /> {pendingControl === "unshare" ? "Closing…" : "Stop sharing"}
                        </Button>
                      )}
                    </div>
                    <blockquote className="mt-5 whitespace-pre-wrap text-xl font-semibold leading-8 text-[#263a5b] sm:text-2xl sm:leading-9">“{sharedResponse.body}”</blockquote>
                    <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#eadfc9] pt-4 text-sm">
                      <strong className="truncate text-[#504837]">{sharedResponse.display_name}</strong>
                      <time className="shrink-0 text-xs text-[#94866c]" dateTime={sharedResponse.updated_at}>{formatResponseTime(sharedResponse.updated_at)}</time>
                    </div>
                  </div>
                ) : isPresenter && !participantView ? (
                  <div>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-y border-[#eadfc9] py-3">
                      <div>
                        <p className="font-bold text-[#263a5b]">{responseCount} {responseCount === 1 ? "response" : "responses"}</p>
                        <p className="text-xs text-[#82755e]">{anonymous ? "Participant identities are hidden from presenters." : "Responses are shown with participant names."}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${submissionsClosed ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>{submissionsClosed ? "Responses closed" : "Collecting"}</span>
                    </div>
                    {responses.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-[#d9c9aa] bg-[#faf5e9] px-5 py-10 text-center text-sm text-[#756951]">{submissionsClosed ? "No responses were submitted for this card." : "Responses will appear here as participants submit them."}</div>
                    ) : (
                      <div className="grid gap-3 md:grid-cols-2" aria-live="polite">
                        {responses.map((item) => submissionsClosed ? (
                          <button
                            key={item.id}
                            type="button"
                            aria-label={`Share response from ${item.display_name} with the group`}
                            onClick={() => void shareResponse(item)}
                            disabled={Boolean(pendingControl)}
                            className="group rounded-2xl border border-[#e3d7c0] bg-white px-4 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#b88b4b] hover:shadow-md disabled:cursor-wait disabled:opacity-70"
                          >
                            <span className="mb-2 flex items-baseline justify-between gap-3">
                              <strong className="truncate text-sm font-bold text-[#263a5b]">{item.display_name}</strong>
                              <time className="shrink-0 text-xs text-[#94866c]" dateTime={item.updated_at}>{formatResponseTime(item.updated_at)}</time>
                            </span>
                            <span className="block whitespace-pre-wrap text-sm leading-6 text-[#504837]">{item.body}</span>
                            <span className="mt-4 flex items-center gap-1.5 border-t border-[#eee4d2] pt-3 text-xs font-bold text-[#8b622e]"><Share2 className="h-3.5 w-3.5" /> Share with group</span>
                          </button>
                        ) : (
                          <article key={item.id} className="rounded-2xl border border-[#e3d7c0] bg-white px-4 py-4 shadow-sm">
                            <div className="mb-2 flex items-baseline justify-between gap-3">
                              <h2 className="truncate text-sm font-bold text-[#263a5b]">{item.display_name}</h2>
                              <time className="shrink-0 text-xs text-[#94866c]" dateTime={item.updated_at}>{formatResponseTime(item.updated_at)}</time>
                            </div>
                            <p className="whitespace-pre-wrap text-sm leading-6 text-[#504837]">{item.body}</p>
                          </article>
                        ))}
                      </div>
                    )}
                    {nextPage && <Button type="button" variant="outline" onClick={loadMore} disabled={loadingMore} className="mt-4 w-full">{loadingMore ? "Loading…" : "Load more responses"}</Button>}
                  </div>
                ) : submissionsClosed ? (
                  <div className="rounded-2xl border border-[#d9c9aa] bg-[#faf5e9] px-5 py-6 text-sm text-[#756951]">
                    <p>Responses are closed for this card.</p>
                    {response && <p className="mt-3 whitespace-pre-wrap">Your response: {response.body}</p>}
                  </div>
                ) : response && !editing ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center" aria-live="polite">
                    <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
                    <h2 className="mt-3 text-xl font-bold text-emerald-950">Response submitted</h2>
                    <p className="mt-1 text-sm text-emerald-800">You can revise it while this card remains open.</p>
                    <div className="mt-5 whitespace-pre-wrap rounded-xl border border-emerald-200 bg-white/80 px-4 py-3 text-left text-sm leading-6 text-[#504837]">{response.body}</div>
                    <Button type="button" variant="outline" onClick={() => { setDraft(response.body); setEditing(true) }} className="mt-5 border-emerald-700 text-emerald-900">
                      <PencilLine className="h-4 w-4" /> Revise response
                    </Button>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="inspire-response" className="text-sm font-bold text-[#263a5b]">Your response</label>
                    <Textarea id="inspire-response" value={draft} maxLength={2000} onChange={(event) => setDraft(event.target.value)} placeholder="Take your time and write as much as feels useful…" className="mt-1 min-h-[76px] resize-y rounded-xl border-[#cdbd9e] bg-white p-2.5 text-sm leading-5 sm:mt-2 sm:min-h-[112px] sm:rounded-2xl sm:p-3 sm:text-sm sm:leading-5" />
                    <div className="mt-1.5 flex items-center justify-between gap-3 text-[11px] text-[#82755e] sm:mt-2 sm:text-xs"><span>Your response can be up to 2,000 characters.</span><span>{draft.length.toLocaleString()}/2,000</span></div>
                    <div data-testid="inspire-listener-actions" className={framed ? "sticky bottom-0 z-10 -mx-2.5 mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-[#eadfc9] bg-[#fffdf8]/95 px-2.5 py-2 backdrop-blur sm:static sm:mx-0 sm:mt-3 sm:gap-3 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none" : "mt-2 flex flex-wrap items-center justify-between gap-2 sm:mt-3 sm:gap-3"}>
                      <span className="flex items-center gap-1.5 text-xs text-[#756951]">
                        {anonymous ? <EyeOff className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        {anonymous ? "Anonymous to the presenter." : "Visible to presenters with your display name."}
                      </span>
                      <Button type="button" onClick={saveResponse} disabled={saving || !draft.trim()} className="min-h-10 bg-[#263a5b] px-5 text-white hover:bg-[#1d2d47] sm:min-h-11 sm:px-6">
                        <Send className="h-4 w-4" /> {saving ? "Submitting…" : response ? "Update response" : "Submit response"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  )
}

function MeetingInspirePanel(props: { preview?: MeetingInspirePanelPreview; embed?: InspireEmbedContext }) {
  const { meetingId } = useParams<{ meetingId: string }>()
  const [params] = useSearchParams()
  const owner = useToolOwnership(`${meetingId}:${params.get('panel_token') || ''}`)
  return <InspireSession key={owner.key} {...props} />
}
export default MeetingInspirePanel
